/**
 * drift.ts — concept-drift detector (EWMA-ECDD on prediction error, v2).
 *
 * Ported from `EWMADriftDetector` in `_proto_kitchen_v2.py`. v2 returns to
 * reflex's prediction-error-based detector (v1 used Page-Hinkley on the
 * reward stream because navigation steps produced no prediction). In v2
 * the head scores BOTH arms at every decision, so a prediction-error stream
 * is dense and clean — exactly the signal a context flip perturbs.
 *
 * Tracks |actual − predicted| reward of the chosen arm. Under stationarity
 * the EWMA error settles to a stable mean with bounded variance; on a
 * context flip the model starts mispredicting → EWMA error rises above the
 * control limit → DRIFT.
 *
 * Method: Ross, Adams, Taft, Hand (2013), the continuous-signal variant of
 * Gama's DDM (2004). Two thresholds (z-scores of current error vs running
 * baseline): WARNING at L_warning (≈95th pct of N(0,1)), DRIFT at L_drift
 * (≈99.7th pct). At DRIFT we open a re-explore window of K forced-random
 * pickup/skip decisions.
 */
import type { DriftStatus } from './constants';

export interface DriftHyperparams {
  lam: number;         // EWMA decay (small = slow, sticky)
  lWarning: number;    // ~95th pct of N(0,1) → 2.0
  lDrift: number;      // ~99.7th pct of N(0,1) → 3.0
  warmup: number;      // ignore the first `warmup` obs (model stabilising)
  exploreWindow: number; // forced-random decision count after a drift
}

export const DEFAULT_DRIFT_HP: DriftHyperparams = {
  lam: 0.05,
  lWarning: 2.0,
  lDrift: 3.0,
  warmup: 8,
  exploreWindow: 6,
};

export class EWMADriftDetector {
  hp: DriftHyperparams;
  private z0 = 0; // baseline EWMA of |error|
  private s0 = 0; // baseline EWMA of error² (for variance)
  private n = 0;
  exploreRemaining = 0;
  nDrift = 0;
  nWarning = 0;
  lastStatus: DriftStatus = 'warmup';

  constructor(hp: Partial<DriftHyperparams> = {}) {
    this.hp = { ...DEFAULT_DRIFT_HP, ...hp };
  }

  /** Full reset for a new seed run. */
  resetRun(): void {
    this.z0 = 0;
    this.s0 = 0;
    this.n = 0;
    this.exploreRemaining = 0;
    this.nDrift = 0;
    this.nWarning = 0;
    this.lastStatus = 'warmup';
  }

  /** Feed one (predicted, actual) reward pair. Returns the status. */
  update(pred: number, actual: number): DriftStatus {
    const err = Math.abs(actual - pred);
    this.n++;
    if (this.n <= this.hp.warmup) {
      // During warmup, accumulate the baseline; never fire.
      this.z0 = (1 - this.hp.lam) * this.z0 + this.hp.lam * err;
      this.s0 = (1 - this.hp.lam) * this.s0 + this.hp.lam * err * err;
      this.lastStatus = 'warmup';
      return 'warmup';
    }
    this.z0 = (1 - this.hp.lam) * this.z0 + this.hp.lam * err;
    this.s0 = (1 - this.hp.lam) * this.s0 + this.hp.lam * err * err;
    const variance = Math.max(1e-9, this.s0 - this.z0 * this.z0);
    const std = Math.sqrt(variance);
    const z = std > 1e-9 ? (err - this.z0) / std : 0;
    if (z > this.hp.lDrift) {
      this.nDrift++;
      this.exploreRemaining = this.hp.exploreWindow;
      this.lastStatus = 'drift';
      return 'drift';
    }
    if (z > this.hp.lWarning) {
      this.nWarning++;
      this.lastStatus = 'warning';
      return 'warning';
    }
    this.lastStatus = 'ok';
    return 'ok';
  }

  /** True if we're in a forced re-explore window after a drift. Consumes one. */
  shouldExplore(): boolean {
    if (this.exploreRemaining > 0) {
      this.exploreRemaining--;
      return true;
    }
    return false;
  }

  /** Current status for HUD display (non-consuming). */
  status(): DriftStatus {
    if (this.exploreRemaining > 0) return 'drift';
    if (this.n <= this.hp.warmup) return 'warmup';
    return this.lastStatus;
  }
}
